import { SourceLinks } from '@/components/ui/source-links';
import { Sparkles, Eye } from 'lucide-react';
import { useState } from 'react';
import { DiscoveryCard } from './discovery-card';

interface SensoryContextCardProps {
  sensoryContext: NonNullable<CultureByPassage['sensoryContext']>;
  isExpanded?: boolean;
  onToggleExpansion?: () => void;
  defaultExpanded?: boolean;
}

/**
 * Shared component for displaying sensory context (setting, sights, sounds, smells, atmosphere)
 * Used in both the cultural context page and the capability modal
 */
export function SensoryContextCard({
  sensoryContext,
  isExpanded: controlledIsExpanded,
  onToggleExpansion: controlledOnToggleExpansion,
  defaultExpanded = false
}: SensoryContextCardProps) {
  const [internalIsExpanded, setInternalIsExpanded] = useState(defaultExpanded);

  const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
  const onToggleExpansion = controlledOnToggleExpansion !== undefined
    ? controlledOnToggleExpansion
    : () => setInternalIsExpanded(!internalIsExpanded);

  const Section = ({ title, content }: { title: string; content?: string }) => {
    if (!content) return null;
    return (
      <div className="space-y-1.5">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 flex items-center gap-1.5">
          <Sparkles className="h-2.5 w-2.5" />
          {title}
        </div>
        <p className="text-sm leading-relaxed text-foreground/80 font-light">
          {content}
        </p>
      </div>
    );
  };

  return (
    <DiscoveryCard
      isExpanded={isExpanded}
      onToggle={onToggleExpansion}
      title="Step Into the Scene"
      icon={Eye}
    >
      <div className="space-y-5 mt-6">
        <Section title="Setting" content={sensoryContext.setting} />
        <Section title="Sights" content={sensoryContext.sights} />
        <Section title="Sounds" content={sensoryContext.sounds} />
        <Section title="Smells" content={sensoryContext.smells} />
        <Section title="Atmosphere" content={sensoryContext.atmosphere} />
      </div>

      {sensoryContext.sources?.length > 0 && (
        <div className="pt-4 border-t border-border/10 mt-6">
          <SourceLinks sources={sensoryContext.sources} />
        </div>
      )}
    </DiscoveryCard>
  );
}
